// Product & Inventory Manager for Business Owners
// Each store sees only their own products (brandId === user.uid)

import React, { useState, useRef, useEffect } from 'react';
import {
  Plus,
  Package,
  Search,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  X,
  Image as ImageIcon,
  DollarSign,
  Tag,
  Loader2,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Check,
  Upload,
  Percent,
  Calendar,
  ShoppingBag,
  History as HistoryIcon,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db, functions } from '../../config/firebase';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  useAdminProducts,
  toggleProductActive,
  updateProduct,
  deleteProduct,
  addProduct,
  uploadProductImage,
  type AdminProduct,
  type ProductSize,
  type NewProductData,
} from '../../hooks/useAdminProducts';
import { ensureBrowserCompatibleImage } from '../../lib/imageUtils';
import { ShopifyImport } from './ShopifyImport';

// ── Constants ────────────────────────────────────────────────────
const PRODUCT_CATEGORIES = [
  'Dresses', 'Tops', 'Bottoms', 'Outerwear', 'Swimwear',
  'Activewear', 'Accessories', 'Shoes', 'Jewelry', 'Bags',
  'Jumpsuits', 'Lingerie', 'Sets', 'Other',
];

const COMMON_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'One Size'];

interface ProductManagerProps {
  businessId: string;
  businessName: string;
  /** Shopify import is an owner-level integration, not a seat task. */
  allowImport?: boolean;
}

// ── Main Component ──────────────────────────────────────────────
export function ProductManager({ businessId, businessName, allowImport = true }: ProductManagerProps) {
  const { products, loading, error } = useAdminProducts(businessId);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showShopifyImport, setShowShopifyImport] = useState(false);
  const [editingProduct, setEditingProduct] = useState<AdminProduct | null>(null);
  const [expandedStock, setExpandedStock] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // 30-day movement KPIs from the immutable inventory ledger
  const [ledger30, setLedger30] = useState<{ sold: number; returned: number } | null>(null);
  useEffect(() => {
    const since = Timestamp.fromMillis(Date.now() - 30 * 24 * 60 * 60 * 1000);
    getDocs(
      query(
        collection(db, 'inventory_ledger'),
        where('businessId', '==', businessId),
        where('createdAt', '>', since),
        orderBy('createdAt', 'desc'),
        limit(1000)
      )
    )
      .then((snap) => {
        let sold = 0;
        let returned = 0;
        snap.forEach((d) => {
          const e = d.data();
          if (e.reason === 'sale') sold += Math.abs(e.delta || 0);
          if (e.reason === 'return_restock') returned += Math.abs(e.delta || 0);
        });
        setLedger30({ sold, returned });
      })
      .catch(() => setLedger30({ sold: 0, returned: 0 }));
  }, [businessId]);

  // Filter products
  const filteredProducts = products.filter((p) => {
    const matchesSearch =
      !searchQuery ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.tags?.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory =
      filterCategory === 'All' || p.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Stats
  const activeCount = products.filter((p) => p.active).length;
  const totalStock = products.reduce((sum, p) => {
    const sizes = p.sizes || [];
    return sum + sizes.reduce((s: number, sz: any) => s + (sz.stockCount || 0), 0);
  }, 0);
  const lowStockCount = products.reduce((count, p) => {
    const sizes = p.sizes || [];
    return count + sizes.filter((sz: any) => (sz.stockCount || 0) > 0 && (sz.stockCount || 0) <= 3).length;
  }, 0);

  // Handlers
  const handleToggleActive = async (product: AdminProduct) => {
    setActionLoading(product.id);
    try {
      await toggleProductActive(product);
    } catch (err) {
      console.error('Failed to toggle product:', err);
    }
    setActionLoading(null);
  };

  const handleDelete = async (product: AdminProduct) => {
    setActionLoading(product.id);
    try {
      await deleteProduct(product);
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete product:', err);
    }
    setActionLoading(null);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-sm text-body">Loading your products...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-4" />
          <p className="text-sm text-body">Failed to load products: {error}</p>
        </CardContent>
      </Card>
    );
  }

  // ── Add/Edit Form Overlay ───────────────────────────────────────
  if (showAddForm || editingProduct) {
    return (
      <ProductForm
        businessId={businessId}
        businessName={businessName}
        product={editingProduct}
        onClose={() => {
          setShowAddForm(false);
          setEditingProduct(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-heading">{products.length}</p>
            <p className="text-[11px] text-body">Total Products</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-success">{activeCount}</p>
            <p className="text-[11px] text-body">Active / Live</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className={`text-xl font-bold ${lowStockCount > 0 ? 'text-amber-500' : 'text-heading'}`}>
              {lowStockCount}
            </p>
            <p className="text-[11px] text-body">Low Stock</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-heading">{totalStock}</p>
            <p className="text-[11px] text-body">Units On Hand</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-heading">
              {ledger30 === null ? '—' : ledger30.sold}
            </p>
            <p className="text-[11px] text-body">Sold (30d)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-heading">
              {ledger30 === null ? '—' : ledger30.returned}
            </p>
            <p className="text-[11px] text-body">Returned (30d)</p>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 h-10 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-primary"
          />
        </div>
        {allowImport && (
          <Button
            variant="outline"
            onClick={() => setShowShopifyImport(true)}
            className="flex-shrink-0 h-10"
          >
            <ShoppingBag className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Shopify</span>
          </Button>
        )}
        <Button onClick={() => setShowAddForm(true)} className="flex-shrink-0 h-10">
          <Plus className="h-4 w-4 mr-1.5" />
          Add Product
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
        {['All', ...PRODUCT_CATEGORIES.filter((c) =>
          products.some((p) => p.category === c)
        )].map((cat) => (
          <button
            key={cat}
            onClick={() => setFilterCategory(cat)}
            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              filterCategory === cat
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-body hover:bg-gray-200'
            }`}
          >
            {cat}
            {cat !== 'All' && (
              <span className="ml-1 opacity-70">
                ({products.filter((p) => p.category === cat).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Empty State */}
      {products.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-serif text-heading mb-2">No products yet</h3>
            <p className="text-sm text-body mb-6 max-w-sm mx-auto">
              Add your first product so shoppers can discover and purchase from your boutique.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Button onClick={() => setShowAddForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Product
              </Button>
              <Button variant="outline" onClick={() => setShowShopifyImport(true)}>
                <ShoppingBag className="h-4 w-4 mr-2" />
                Import from Shopify
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Product List */}
      {filteredProducts.length > 0 && (
        <div className="space-y-3">
          {filteredProducts.map((product) => (
            <Card key={product.id} className={!product.active ? 'opacity-60' : ''}>
              <CardContent className="p-3">
                <div className="flex gap-3">
                  {/* Image */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-6 w-6 text-gray-300" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h4 className="text-sm font-medium text-heading truncate">
                          {product.name}
                        </h4>
                        <p className="text-xs text-body">{product.category}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {product.compareAtPrice && product.compareAtPrice > product.price ? (
                          <>
                            <p className="text-[11px] text-gray-400 line-through">
                              ${product.compareAtPrice}
                            </p>
                            <p className="text-sm font-bold text-red-600">
                              ${product.price}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm font-bold text-heading">
                            ${product.price}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Size/Stock Summary */}
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {(product.sizes || []).slice(0, 5).map((size: any, i: number) => {
                        const stock = size.stockCount ?? (size.available ? 1 : 0);
                        return (
                          <span
                            key={i}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              stock === 0
                                ? 'bg-red-100 text-red-600'
                                : stock <= 3
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {size.label}: {stock}
                          </span>
                        );
                      })}
                    </div>

                    {/* Tags */}
                    {product.tags && product.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {product.tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="text-[10px] text-primary bg-primary/5 px-1.5 py-0.5 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-1.5 mt-2">
                      <button
                        onClick={() => handleToggleActive(product)}
                        disabled={actionLoading === product.id}
                        className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                          product.active
                            ? 'bg-success/10 text-success hover:bg-success/20'
                            : 'bg-gray-100 text-body hover:bg-gray-200'
                        }`}
                      >
                        {actionLoading === product.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : product.active ? (
                          <Eye className="h-3 w-3" />
                        ) : (
                          <EyeOff className="h-3 w-3" />
                        )}
                        {product.active ? 'Live' : 'Hidden'}
                      </button>

                      <button
                        onClick={() => setExpandedStock(expandedStock === product.id ? null : product.id)}
                        className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg text-[11px] font-medium text-body hover:bg-gray-200 transition-colors"
                      >
                        <Package className="h-3 w-3" />
                        Stock
                        {expandedStock === product.id ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )}
                      </button>

                      <button
                        onClick={() => setEditingProduct(product)}
                        className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg text-[11px] font-medium text-body hover:bg-gray-200 transition-colors"
                      >
                        <Edit3 className="h-3 w-3" />
                        Edit
                      </button>

                      {deleteConfirm === product.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(product)}
                            disabled={actionLoading === product.id}
                            className="px-2 py-1 bg-red-500 text-white rounded-lg text-[11px] font-medium hover:bg-red-600 transition-colors"
                          >
                            {actionLoading === product.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              'Confirm'
                            )}
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="px-2 py-1 bg-gray-100 rounded-lg text-[11px] font-medium text-body hover:bg-gray-200"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(product.id)}
                          className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg text-[11px] font-medium text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Stock Management */}
                {expandedStock === product.id && (
                  <StockEditor product={product} businessId={businessId} />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* No results for filter */}
      {products.length > 0 && filteredProducts.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <Search className="h-8 w-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-body">No products match your search</p>
          </CardContent>
        </Card>
      )}

      {showShopifyImport && (
        <ShopifyImport
          businessId={businessId}
          onClose={() => setShowShopifyImport(false)}
          onImportComplete={() => setShowShopifyImport(false)}
        />
      )}
    </div>
  );
}

// ── Stock Editor (ledgered adjustments with reason codes) ───────
const ADJUST_REASONS = [
  { id: 'manual_restock', label: 'Restock — received units' },
  { id: 'recount', label: 'Recount / cycle count' },
  { id: 'damage', label: 'Damaged' },
  { id: 'shrinkage', label: 'Shrinkage / loss' },
  { id: 'other', label: 'Other' },
];

const LEDGER_REASON_LABELS: Record<string, string> = {
  sale: 'Sale',
  return_restock: 'Return',
  manual_restock: 'Restock',
  recount: 'Recount',
  damage: 'Damage',
  shrinkage: 'Shrinkage',
  other: 'Adjustment',
  shopify_sync: 'Shopify sync',
  initial_stock: 'Initial stock',
  untracked_edit: 'Direct edit',
  reconcile_heal: 'Reconciliation',
  channel_sale: 'Channel sale',
  partner_sale: 'Partner sale',
  partner_receipt: 'Partner receipt',
  transfer_out: 'Transfer out',
  transfer_in: 'Transfer in',
};


function StockEditor({ product, businessId }: { product: AdminProduct; businessId: string }) {
  const sizes: any[] = product.sizes || [];
  const [counts, setCounts] = useState<Record<string, string>>(() =>
    Object.fromEntries(sizes.map((s: any) => [s.label, String(s.stockCount ?? 0)]))
  );
  const [reason, setReason] = useState('manual_restock');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState<Array<{ locationId: string; name: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const snap = await getDocs(query(collection(db, 'stock_locations'), where('businessId', '==', businessId)));
      if (cancelled) return;
      const rows = snap.docs.map((d) => ({ locationId: d.id, name: d.data().name || d.id }));
      setLocations(rows);
      const def = snap.docs.find((d) => d.data().isDefault);
      setLocationId(def?.id || rows[0]?.locationId || '');
    })();
    return () => { cancelled = true; };
  }, [businessId]);


  const changedSizes = sizes.filter((s: any) => {
    const next = parseInt(counts[s.label] ?? '', 10);
    return Number.isFinite(next) && next >= 0 && next !== (s.stockCount ?? 0);
  });

  const handleSave = async () => {
    if (changedSizes.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const adjust = httpsCallable(functions, 'adjustInventory');
      await adjust({
        productId: product.id,
        locationId: locationId || undefined,
        adjustments: changedSizes.map((s: any) => ({
          size: s.label,
          newCount: parseInt(counts[s.label], 10),
          reason,
          note: note.trim() || undefined,
        })),
      });
      setSaved(true);
      setNote('');
      setTimeout(() => setSaved(false), 2500);
    } catch (err: any) {
      console.error('Failed to adjust stock:', err);
      setError(err?.message || 'Adjustment failed');
    }
    setSaving(false);
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-body uppercase tracking-wider">
          Adjust Stock
        </p>
        {locations.length > 0 && (
          <select
            className="text-[11px] border rounded-md h-7 px-1"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
          >
            {locations.map((l) => (
              <option key={l.locationId} value={l.locationId}>{l.name}</option>
            ))}
          </select>
        )}
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary-hover"
        >
          <HistoryIcon className="h-3 w-3" />
          {showHistory ? 'Hide history' : 'Movement history'}
        </button>
      </div>

      {showHistory && <MovementHistory productId={product.id} />}

      <div className="space-y-2">
        {sizes.map((size: any) => {
          const onHand = size.stockCount ?? 0;
          const reserved = size.reservedCount ?? 0;
          const next = parseInt(counts[size.label] ?? '', 10);
          const delta = Number.isFinite(next) ? next - onHand : 0;
          return (
            <div key={size.label} className="flex items-center gap-2">
              <span className="w-16 text-xs font-medium text-heading text-center bg-gray-50 rounded-lg py-1.5">
                {size.label}
              </span>
              <span className="text-[10px] text-body w-20">
                On hand: <span className="font-semibold">{onHand}</span>
              </span>
              {reserved > 0 && (
                <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                  {reserved} reserved
                </span>
              )}
              <div className="flex-1 flex items-center justify-end gap-1.5">
                <span className="text-[10px] text-body">New:</span>
                <input
                  type="number"
                  min="0"
                  value={counts[size.label] ?? ''}
                  onChange={(e) => {
                    setCounts((prev) => ({ ...prev, [size.label]: e.target.value }));
                    setSaved(false);
                  }}
                  className="w-16 px-2 py-1.5 border border-gray-200 rounded-lg text-xs text-center focus:outline-none focus:border-primary"
                />
                {delta !== 0 && (
                  <span
                    className={`text-[10px] font-semibold w-8 text-center ${
                      delta > 0 ? 'text-success' : 'text-red-500'
                    }`}
                  >
                    {delta > 0 ? `+${delta}` : delta}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {sizes.length === 0 && (
        <p className="text-xs text-body text-center py-3">
          No sizes configured. Use Edit to add sizes first.
        </p>
      )}

      {changedSizes.length > 0 && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-primary bg-white"
            >
              {ADJUST_REASONS.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note (optional)"
              className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-primary"
            />
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-500 mt-2">{error}</p>}

      <div className="flex items-center justify-end gap-2 mt-3">
        {saved && (
          <span className="flex items-center gap-1 text-xs text-success">
            <Check className="h-3.5 w-3.5" />
            Recorded in ledger
          </span>
        )}
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving || changedSizes.length === 0}
          className="text-xs h-8"
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
          ) : (
            <Check className="h-3.5 w-3.5 mr-1.5" />
          )}
          Apply Adjustment
        </Button>
      </div>
    </div>
  );
}

// ── Movement History (reads the immutable ledger) ───────────────
function MovementHistory({ productId }: { productId: string }) {
  const [entries, setEntries] = useState<any[] | null>(null);

  useEffect(() => {
    getDocs(
      query(
        collection(db, 'inventory_ledger'),
        where('productId', '==', productId),
        orderBy('createdAt', 'desc'),
        limit(25)
      )
    )
      .then((snap) => setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch((err) => {
        console.error('Failed to load movement history:', err);
        setEntries([]);
      });
  }, [productId]);

  if (entries === null) {
    return (
      <div className="py-4 text-center">
        <Loader2 className="h-4 w-4 animate-spin mx-auto text-gray-400" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <p className="text-xs text-body text-center py-3 mb-2 bg-gray-50 rounded-lg">
        No stock movements recorded yet.
      </p>
    );
  }

  return (
    <div className="mb-3 max-h-56 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
      {entries.map((e) => {
        const ts = e.createdAt?.toDate ? e.createdAt.toDate() : null;
        return (
          <div key={e.id} className="flex items-center gap-2 px-2.5 py-1.5 text-[11px]">
            <span
              className={`font-bold w-9 text-center ${
                e.delta > 0 ? 'text-success' : 'text-red-500'
              }`}
            >
              {e.delta > 0 ? `+${e.delta}` : e.delta}
            </span>
            <span className="w-10 text-center text-body font-medium">{e.size}</span>
            <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
              {LEDGER_REASON_LABELS[e.reason] || e.reason}
            </span>
            <span className="text-gray-400">
              {e.before} → {e.after}
            </span>
            {e.note && (
              <span className="text-gray-400 italic truncate flex-1" title={e.note}>
                {e.note}
              </span>
            )}
            <span className="ml-auto text-gray-400 whitespace-nowrap">
              {ts
                ? ts.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
                  ' ' +
                  ts.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
                : ''}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Add/Edit Product Form ───────────────────────────────────────
function ProductForm({
  businessId,
  businessName,
  product,
  onClose,
}: {
  businessId: string;
  businessName: string;
  product: AdminProduct | null;
  onClose: () => void;
}) {
  const isEdit = Boolean(product);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(product?.name || '');
  const [description, setDescription] = useState(product?.description || '');
  const [price, setPrice] = useState(product?.price?.toString() || '');
  const [compareAtPrice, setCompareAtPrice] = useState(product?.compareAtPrice?.toString() || '');
  const [enableSchedule, setEnableSchedule] = useState(Boolean(product?.saleSchedule));
  const [scheduleStart, setScheduleStart] = useState(product?.saleSchedule?.startDate || '');
  const [scheduleEnd, setScheduleEnd] = useState(product?.saleSchedule?.endDate || '');
  const [category, setCategory] = useState(product?.category || '');
  const [tagsInput, setTagsInput] = useState(product?.tags?.join(', ') || '');
  const [imageUrls, setImageUrls] = useState<string[]>(product?.images || []);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [sizes, setSizes] = useState<ProductSize[]>(
    product?.sizes
      ? (product.sizes as any[]).map((s) => ({
          label: s.label || '',
          sku: s.sku || '',
          stockCount: s.stockCount ?? (s.available ? 10 : 0),
          reservedCount: s.reservedCount || 0,
          committedCount: s.committedCount || 0,
          returnedCount: s.returnedCount || 0,
          lowStockAlert: s.lowStockAlert || false,
          available: s.available ?? true,
        }))
      : COMMON_SIZES.slice(0, 5).map((label) => ({
          label,
          sku: '',
          stockCount: 0,
          reservedCount: 0,
          committedCount: 0,
          returnedCount: 0,
          lowStockAlert: false,
          available: false,
        }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const converted = await Promise.all(files.map(ensureBrowserCompatibleImage));
    setImageFiles((prev) => [...prev, ...converted]);

    converted.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImagePreviews((prev) => [...prev, ev.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index: number, isExisting: boolean) => {
    if (isExisting) {
      setImageUrls((prev) => prev.filter((_, i) => i !== index));
    } else {
      const adjIndex = index - imageUrls.length;
      setImageFiles((prev) => prev.filter((_, i) => i !== adjIndex));
      setImagePreviews((prev) => prev.filter((_, i) => i !== adjIndex));
    }
  };

  const handleSizeChange = (index: number, field: string, value: string | number) => {
    setSizes((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'stockCount') {
        updated[index].available = Number(value) > 0;
      }
      return updated;
    });
  };

  const handleAddSize = () => {
    setSizes((prev) => [
      ...prev,
      {
        label: '',
        sku: '',
        stockCount: 0,
        reservedCount: 0,
        committedCount: 0,
        returnedCount: 0,
        lowStockAlert: false,
        available: false,
      },
    ]);
  };

  const handleRemoveSize = (index: number) => {
    setSizes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!name.trim()) return setError('Product name is required');
    if (!price || parseFloat(price) <= 0) return setError('Enter a valid price');
    if (!category) return setError('Select a category');
    if (sizes.filter((s) => s.label.trim()).length === 0)
      return setError('Add at least one size');

    setSaving(true);
    try {
      const tags = tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const validSizes = sizes.filter((s) => s.label.trim());

      const parsedCompareAt = compareAtPrice ? parseFloat(compareAtPrice) : null;
      const parsedPrice = parseFloat(price);
      const saleSchedule = enableSchedule && scheduleStart && scheduleEnd
        ? { startDate: scheduleStart, endDate: scheduleEnd }
        : null;
      const isOnSale = Boolean(parsedCompareAt && parsedCompareAt > parsedPrice);

      if (isEdit && product) {
        let newImageUrls: string[] = [];
        if (imageFiles.length > 0) {
          const uploads = imageFiles.map((f) => uploadProductImage(businessId, f));
          newImageUrls = await Promise.all(uploads);
        }

        await updateProduct(product.id, {
          name: name.trim(),
          description: description.trim(),
          price: parsedPrice,
          compareAtPrice: parsedCompareAt,
          saleSchedule,
          sale: isOnSale,
          category,
          tags,
          sizes: validSizes,
          images: [...imageUrls, ...newImageUrls],
        } as any);
      } else {
        await addProduct(businessId, businessName, {
          name: name.trim(),
          description: description.trim(),
          price: parsedPrice,
          compareAtPrice: parsedCompareAt,
          saleSchedule,
          category,
          tags,
          sizes: validSizes,
          images: imageUrls,
          imageFiles,
        });
      }

      onClose();
    } catch (err: any) {
      console.error('Failed to save product:', err);
      setError(err.message || 'Failed to save product');
    }
    setSaving(false);
  };

  const allImages = [
    ...imageUrls.map((url) => ({ url, isExisting: true })),
    ...imagePreviews.map((url) => ({ url, isExisting: false })),
  ];

  return (
    <Card>
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-lg font-serif text-heading">
            {isEdit ? 'Edit Product' : 'Add New Product'}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-body" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}

          {/* Images */}
          <div>
            <label className="text-xs font-medium text-body uppercase tracking-wider mb-2 block">
              Photos
            </label>
            <div className="flex gap-2 flex-wrap">
              {allImages.map((img, i) => (
                <div
                  key={i}
                  className="relative w-20 h-20 rounded-lg overflow-hidden bg-gray-100 group"
                >
                  <img
                    src={img.url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(i, img.isExisting)}
                    className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                  >
                    <X className="h-4 w-4 text-white" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <Upload className="h-5 w-5 text-gray-400 mb-0.5" />
                <span className="text-[10px] text-body">Upload</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="hidden"
              />
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="text-xs font-medium text-body uppercase tracking-wider mb-1.5 block">
              Product Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Silk Slip Dress"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-medium text-body uppercase tracking-wider mb-1.5 block">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your product..."
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary resize-none"
            />
          </div>

          {/* Pricing */}
          <div>
            <label className="text-xs font-medium text-body uppercase tracking-wider mb-1.5 block">
              Pricing
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-body mb-1 block">Price *</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-body mb-1 block">Compare-at price</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={compareAtPrice}
                    onChange={(e) => setCompareAtPrice(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
            </div>

            {compareAtPrice && parseFloat(compareAtPrice) > 0 && parseFloat(price) > 0 && parseFloat(compareAtPrice) > parseFloat(price) && (
              <div className="mt-2 flex items-center gap-2 p-2.5 bg-red-50 border border-red-100 rounded-xl">
                <Percent className="h-4 w-4 text-red-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-red-700">
                    Sale: {Math.round(((parseFloat(compareAtPrice) - parseFloat(price)) / parseFloat(compareAtPrice)) * 100)}% off
                  </p>
                  <p className="text-[11px] text-red-500">
                    Shoppers will see <span className="line-through">${parseFloat(compareAtPrice).toFixed(2)}</span>{' '}
                    <span className="font-bold">${parseFloat(price).toFixed(2)}</span>
                  </p>
                </div>
              </div>
            )}

            {compareAtPrice && parseFloat(compareAtPrice) > 0 && parseFloat(price) > 0 && parseFloat(compareAtPrice) <= parseFloat(price) && (
              <p className="mt-1.5 text-[11px] text-amber-600">
                Compare-at price must be higher than the selling price to show a discount.
              </p>
            )}

            {/* Schedule Discount */}
            {compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price) && (
              <div className="mt-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableSchedule}
                    onChange={(e) => setEnableSchedule(e.target.checked)}
                    className="rounded border-gray-300 text-primary focus:ring-primary h-3.5 w-3.5"
                  />
                  <span className="text-[11px] font-medium text-body flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Schedule this discount
                  </span>
                </label>

                {enableSchedule && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <label className="text-[10px] text-body mb-0.5 block">Start date</label>
                      <input
                        type="datetime-local"
                        value={scheduleStart}
                        onChange={(e) => setScheduleStart(e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-body mb-0.5 block">End date</label>
                      <input
                        type="datetime-local"
                        value={scheduleEnd}
                        onChange={(e) => setScheduleEnd(e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-primary"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="text-xs font-medium text-body uppercase tracking-wider mb-1.5 block">
              Category *
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary bg-white appearance-none"
            >
              <option value="">Select...</option>
              {PRODUCT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="text-xs font-medium text-body uppercase tracking-wider mb-1.5 block">
              Tags
              <span className="font-normal text-gray-400 ml-1">(comma separated)</span>
            </label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="summer, casual, linen"
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Sizes & Stock */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-body uppercase tracking-wider">
                Sizes & Stock *
              </label>
              <button
                type="button"
                onClick={handleAddSize}
                className="flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary-hover"
              >
                <Plus className="h-3 w-3" />
                Add Size
              </button>
            </div>

            {/* Quick-add common sizes */}
            <div className="flex gap-1 mb-2 flex-wrap">
              {COMMON_SIZES.map((s) => {
                const exists = sizes.some((sz) => sz.label === s);
                return (
                  <button
                    key={s}
                    type="button"
                    disabled={exists}
                    onClick={() => {
                      if (!exists) {
                        setSizes((prev) => [
                          ...prev,
                          {
                            label: s,
                            sku: '',
                            stockCount: 0,
                            reservedCount: 0,
                            committedCount: 0,
                            returnedCount: 0,
                            lowStockAlert: false,
                            available: false,
                          },
                        ]);
                      }
                    }}
                    className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                      exists
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-primary/10 text-primary hover:bg-primary/20 cursor-pointer'
                    }`}
                  >
                    + {s}
                  </button>
                );
              })}
            </div>

            {isEdit && (
              <p className="text-[10px] text-body mb-2 bg-gray-50 rounded-lg px-2 py-1.5">
                Stock for existing sizes is adjusted from the product list's{' '}
                <span className="font-medium">Stock</span> panel, where every change is
                recorded with a reason.
              </p>
            )}

            <div className="space-y-2">
              {sizes.map((size, i) => {
                const stockLocked =
                  isEdit &&
                  (product?.sizes as any[] | undefined)?.some(
                    (s: any) => s.label === size.label
                  );
                return (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={size.label}
                      onChange={(e) => handleSizeChange(i, 'label', e.target.value)}
                      placeholder="Size"
                      className="w-20 px-2 py-1.5 border border-gray-200 rounded-lg text-xs text-center focus:outline-none focus:border-primary"
                    />
                    <span className="text-[10px] text-body">Qty:</span>
                    <input
                      type="number"
                      min="0"
                      value={size.stockCount}
                      disabled={stockLocked}
                      title={stockLocked ? 'Adjust via the Stock panel (ledgered)' : undefined}
                      onChange={(e) =>
                        handleSizeChange(i, 'stockCount', parseInt(e.target.value) || 0)
                      }
                      className={`w-16 px-2 py-1.5 border border-gray-200 rounded-lg text-xs text-center focus:outline-none focus:border-primary ${
                        stockLocked ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveSize(i)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Submit */}
          <div className="flex items-center gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : isEdit ? (
                'Save Changes'
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
